import type {
  AttachmentDto,
  AttachmentUrlsDto,
  ConversationDto,
  ConversationListDto,
  MessageListDto,
  PresignRequest,
  PresignResponseDto,
  Seq,
  UnreadCountDto,
} from "../contract/chat.events";
import type { ConversationFilter } from "../lib/queryKeys";
import {
  conversationsControllerCreate,
  conversationsControllerGetOne,
  conversationsControllerList,
  messagesControllerAfter,
  messagesControllerBefore,
  messagesControllerUnreadCount,
  uploadsControllerComplete,
  uploadsControllerPresign,
  uploadsControllerUrls,
} from "./generated/chat";

/**
 * The only door from chat hooks to REST. Components never know which
 * implementation is behind it:
 *   REAL — the Orval client (src/chat/api/generated), through the app's axios
 *          instance with its auth/refresh interceptors;
 *   MOCK — an in-memory backend for local UI work, VITE_CHAT_MOCK=true.
 */
export interface ChatApi {
  createConversation(listingId: number): Promise<ConversationDto>;
  listConversations(params: { cursor?: string; limit?: number; filter?: ConversationFilter }): Promise<ConversationListDto>;
  getConversation(id: string): Promise<ConversationDto>;
  messagesBefore(id: string, params: { beforeSeq?: Seq; limit?: number }): Promise<MessageListDto>;
  messagesAfter(id: string, params: { afterSeq: Seq; limit?: number }): Promise<MessageListDto>;
  unreadCount(): Promise<UnreadCountDto>;
  presign(request: PresignRequest): Promise<PresignResponseDto>;
  completeUpload(attachmentId: string): Promise<AttachmentDto>;
  attachmentUrls(attachmentId: string): Promise<AttachmentUrlsDto>;
}

// The generated response types are checked against the contract types here:
// a backend change that drifts from chat.events.ts fails the type check.
const realChatApi: ChatApi = {
  createConversation: (listingId) => conversationsControllerCreate({ listingId }),
  listConversations: (params) => conversationsControllerList(params),
  getConversation: (id) => conversationsControllerGetOne(id),
  messagesBefore: (id, params) => messagesControllerBefore(id, params),
  messagesAfter: (id, params) => messagesControllerAfter(id, params),
  unreadCount: () => messagesControllerUnreadCount(),
  presign: (request) => uploadsControllerPresign(request),
  completeUpload: (attachmentId) => uploadsControllerComplete(attachmentId),
  attachmentUrls: (attachmentId) => uploadsControllerUrls(attachmentId),
};

export const CHAT_MOCK = import.meta.env.VITE_CHAT_MOCK === "true";

/** Mock methods load the mock module on first use — normal builds never contain it. */
function lazyMock(): ChatApi {
  const load = () => import("./mock/mockServer").then((m) => m.mockChatApi);
  return {
    createConversation: async (...a) => (await load()).createConversation(...a),
    listConversations: async (...a) => (await load()).listConversations(...a),
    getConversation: async (...a) => (await load()).getConversation(...a),
    messagesBefore: async (...a) => (await load()).messagesBefore(...a),
    messagesAfter: async (...a) => (await load()).messagesAfter(...a),
    unreadCount: async (...a) => (await load()).unreadCount(...a),
    presign: async (...a) => (await load()).presign(...a),
    completeUpload: async (...a) => (await load()).completeUpload(...a),
    attachmentUrls: async (...a) => (await load()).attachmentUrls(...a),
  };
}

export const chatApi: ChatApi = CHAT_MOCK ? lazyMock() : realChatApi;
